import React, { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { forgotPassword, confirmForgotPassword, completeNewPassword } from '../services/auth';

type View = 'login' | 'forgot' | 'reset' | 'newpassword';

const LoginPage: React.FC = () => {
  const { user, login, error: authError, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
  if (user && !loading) {
    navigate(from, { replace: true });
  }

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const result = await login(email, password);
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setMessage('You must set a new password to continue.');
        setView('newpassword');
        setNewPassword('');
      } else if (result.isSignedIn) {
        navigate(from, { replace: true });
      } else {
        setError('Sign-in was not completed. Please try again.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!newPassword || !displayName) return;
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const result = await completeNewPassword(newPassword, displayName);
      if (result.isSignedIn) {
        // User is already signed in after confirmSignIn — just reload the page
        // to let AuthContext pick up the session
        window.location.href = from;
      } else {
        setError('Password change was not completed. Please try again.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to set new password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      await forgotPassword(email);
      setMessage('Verification code sent to your email.');
      setView('reset');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !code || !newPassword) return;
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      await confirmForgotPassword(email, code, newPassword);
      setMessage('Password reset successful. Please sign in.');
      setView('login');
      setPassword('');
      setCode('');
      setNewPassword('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page" style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Yacht Race Verification Portal</h1>
        <p style={styles.subtitle}>
          {view === 'login' && 'Sign in to manage your race documentation'}
          {view === 'forgot' && 'Enter your email to receive a reset code'}
          {view === 'reset' && 'Enter the code and your new password'}
          {view === 'newpassword' && 'You must set a new password to continue'}
        </p>

        {message && <p style={styles.success}>{message}</p>}
        {(error || authError) && <p role="alert" style={styles.error}>{error || authError}</p>}

        {view === 'login' && (
          <form onSubmit={handleLogin} style={styles.form}>
            <label htmlFor="email" style={styles.label}>Email</label>
            <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" required autoComplete="username" style={styles.input} />
            <label htmlFor="password" style={styles.label}>Password</label>
            <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password" required autoComplete="current-password" style={styles.input} />
            <button type="submit" disabled={submitting || loading} style={styles.button}>
              {submitting ? 'Signing in…' : 'Sign In'}
            </button>
            <button type="button" onClick={() => { setView('forgot'); setError(null); setMessage(null); }}
              style={styles.link}>Forgot password?</button>
            {process.env.NODE_ENV === 'development' && (
              <button
                type="button"
                data-testid="test-login-btn"
                onClick={() => {
                  setEmail('user@example.com');
                  setPassword('Pass123!');
                }}
                style={{ ...styles.link, marginTop: '0.5rem', color: '#999', fontSize: '0.8rem' }}
              >
                Fill test credentials
              </button>
            )}
          </form>
        )}

        {view === 'forgot' && (
          <form onSubmit={handleForgotPassword} style={styles.form}>
            <label htmlFor="reset-email" style={styles.label}>Email</label>
            <input id="reset-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" required style={styles.input} />
            <button type="submit" disabled={submitting} style={styles.button}>
              {submitting ? 'Sending…' : 'Send Reset Code'}
            </button>
            <button type="button" onClick={() => { setView('login'); setError(null); setMessage(null); }}
              style={styles.link}>Back to sign in</button>
          </form>
        )}

        {view === 'reset' && (
          <form onSubmit={handleResetPassword} style={styles.form}>
            <label htmlFor="code" style={styles.label}>Verification Code</label>
            <input id="code" type="text" value={code} onChange={e => setCode(e.target.value)}
              placeholder="Enter code from email" required style={styles.input} />
            <label htmlFor="new-password" style={styles.label}>New Password</label>
            <input id="new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter new password" required autoComplete="new-password" style={styles.input} />
            <button type="submit" disabled={submitting} style={styles.button}>
              {submitting ? 'Resetting…' : 'Reset Password'}
            </button>
            <button type="button" onClick={() => { setView('login'); setError(null); setMessage(null); }}
              style={styles.link}>Back to sign in</button>
          </form>
        )}

        {view === 'newpassword' && (
          <form onSubmit={handleNewPassword} style={styles.form}>
            <label htmlFor="display-name" style={styles.label}>Your Name</label>
            <input id="display-name" type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
              placeholder="Enter your full name" required style={styles.input} />
            <label htmlFor="forced-new-password" style={styles.label}>New Password</label>
            <input id="forced-new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="Choose a new password" required autoComplete="new-password" style={styles.input} />
            <button type="submit" disabled={submitting} style={styles.button}>
              {submitting ? 'Setting password…' : 'Set New Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' },
  card: { backgroundColor: '#fff', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' },
  title: { textAlign: 'center' as const, marginBottom: '0.25rem', color: '#1a3a5c', fontSize: '1.5rem' },
  subtitle: { textAlign: 'center' as const, marginBottom: '1.5rem', color: '#666', fontSize: '0.9rem' },
  form: { display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' },
  label: { fontWeight: 600, fontSize: '0.875rem', color: '#333' },
  input: { padding: '0.625rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '1rem' },
  error: { color: '#d32f2f', fontSize: '0.875rem', margin: 0 },
  success: { color: '#2e7d32', fontSize: '0.875rem', margin: 0 },
  button: { marginTop: '0.5rem', padding: '0.75rem', backgroundColor: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' },
  link: { background: 'none', border: 'none', color: '#1a3a5c', cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'underline', padding: 0, textAlign: 'center' as const },
};

export default LoginPage;
